import { ContentCard, Tabs, TabsProps } from '@indigo-ag/component-module';
import { FinancingContact, OpportunityContactRole } from '@indigo-ag/schema';
import i18next from 'i18next';
import React, { useState } from 'react';
import styled from 'styled-components';

import { ContactDetailsGrid } from '../../../components/BrazilGrowerContacts/ContactDetailsGrid';
import { OpportunityContactRoleType } from '../types';
import { getOpportunityContactRoleType, getTranslatedRole } from '../utils';
import { BackgroundCheckCard } from './BackgroundCheckCard';
import { CreditOverview } from './CreditOverview';
import { RelationshipsTable } from './RelationshipsTable';

enum TabId {
  SUMMARY = 'SUMMARY',
  RELATIONSHIPS = 'RELATIONSHIPS',
  CREDIT_OVERVIEW = 'CREDIT_OVERVIEW'
}

const StyledTabs = styled<(props: TabsProps<TabId>) => JSX.Element>(Tabs)`
  margin-bottom: 16px;
`;

const CardContainer = styled.div`
  > div {
    margin-bottom: 16px;
  }
`;

interface Props {
  contact: FinancingContact;
  contactRoles: OpportunityContactRole[];
  selectedRole: OpportunityContactRole;
}

export const ContactDetailTabs = ({ contact, contactRoles, selectedRole: { role, contactId } }: Props) => {
  const [tabId, setTabId] = useState(TabId.SUMMARY);
  const contactRoleLabel = getTranslatedRole(getOpportunityContactRoleType(role) ?? role);

  const getPageContent = (tabId: TabId) => {
    switch (tabId) {
      case TabId.SUMMARY:
        return (
          <CardContainer>
            <ContentCard titleText={contactRoleLabel}>
              <ContactDetailsGrid contact={contact} />
            </ContentCard>
            {contact.taxIdentifier ? (
              <BackgroundCheckCard taxIdentifier={contact.taxIdentifier} title={i18next.t('backgroundCheck_title')} />
            ) : null}
          </CardContainer>
        );
      case TabId.RELATIONSHIPS:
        return <RelationshipsTable contactRoles={contactRoles.filter(r => r.contactId !== contactId)} />;
      case TabId.CREDIT_OVERVIEW:
        return <CreditOverview taxIdentifier={contact.taxIdentifier} />;
      default:
        return <></>;
    }
  };

  var items = [
    {
      id: TabId.SUMMARY,
      title: contactRoleLabel
    },
    { id: TabId.RELATIONSHIPS, title: i18next.t('relationships') }
  ];

  const isPrimary = getOpportunityContactRoleType(role) === OpportunityContactRoleType.PRIMARY;
  if (isPrimary) {
    items.push({ id: TabId.CREDIT_OVERVIEW, title: i18next.t('creditOverview') });
  }

  return (
    <StyledTabs
      activeTabId={tabId}
      ariaLabel="Contact Detail Tabs"
      items={items}
      onChange={setTabId}
      renderPanel={getPageContent}
    />
  );
};
